// Partner cockpit — program management across the portfolio (B5B-PARTNER-COCKPIT §6). Implementation
// programs across every mandated tenant, with status + timeline. Behind real auth.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { cockpit, CockpitError, type PortfolioProgram } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  active: "default", in_progress: "default", completed: "secondary", draft: "outline",
};

function fmt(d: string | null): string { return d ? new Date(d).toLocaleDateString() : "—"; }

const COLUMNS: Column<PortfolioProgram>[] = [
  { key: "name", header: "Program", render: (r) => <span className="font-medium">{r.name}</span> },
  { key: "tenant", header: "Tenant", render: (r) => <Link to="/tenant/$tenantId" params={{ tenantId: r.tenantId }} onClick={(e) => e.stopPropagation()} className="hover:underline"><Badge variant="outline">{r.tenantName}</Badge></Link> },
  { key: "status", header: "Status", render: (r) => <Badge variant={STATUS_VARIANT[r.status] ?? "secondary"}>{humanize(r.status)}</Badge> },
  { key: "started", header: "Started", render: (r) => <span className="text-muted-foreground">{fmt(r.startedAt)}</span> },
  { key: "due", header: "Target", render: (r) => <span className="text-muted-foreground">{fmt(r.expectedCompletionAt)}</span> },
];

export function ProgramsRoute() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-programs"], queryFn: () => cockpit.programs(), retry: false });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Programs" description="Implementation programs across your mandated tenants." />
      {error instanceof CockpitError && error.status === 401 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Sign in as a firm consultant to view programs.</CardContent></Card>
      ) : error ? (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      ) : (
        <DataTable<PortfolioProgram> columns={COLUMNS} rows={data ?? []} loading={isLoading} getRowId={(r) => r.id} onRowClick={(r) => navigate({ to: "/program/$programId", params: { programId: r.id } })} empty="No programs across your portfolio yet." />
      )}
    </div>
  );
}
